import React from 'react';
import { Button, FeedbackIcon, Pagination, SparkIcon, Spinner, Text, TooltipTrigger } from '@spark';
import DiffSection from './DiffSection';
import DiffText from './DiffText';
import ClampedContent from './ClampedContent';
import {
  AnnotationTagRow,
  LabelTransition,
  buildAnnotationTags,
  buildSingleLabelTransition,
} from './annotationTags';
import './versionsDiff.css';

/** Page items for Spark Pagination: all pages up to 7, otherwise 1 … window … last. */
export function buildPageItems(totalPages, currentPage) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  const window = [currentPage - 1, currentPage, currentPage + 1].filter(
    (page) => page > 1 && page < totalPages,
  );
  const items = [1];
  if (window[0] > 2) items.push('ellipsis');
  items.push(...window);
  if (window[window.length - 1] < totalPages - 1) items.push('ellipsis');
  items.push(totalPages);
  return items;
}

const ReviewedState = ({ verified, struck = false }) => (
  <span className={struck ? 'vdm-reviewed-state vdm-reviewed-state--old' : 'vdm-reviewed-state'}>
    {verified ? <FeedbackIcon feedbackType="success" size={16} /> : null}
    <Text variant="base-regular" color={verified ? 'primary' : 'secondary'}>
      {verified ? 'Verified' : 'Not-Verified'}
    </Text>
  </span>
);

/** REVIEWED cell: static state, or `old → new` transition with the old state struck through. */
const ReviewedCell = ({ record }) => {
  const fromReviewed = record.from?.is_validated;
  const toReviewed = record.to?.is_validated;
  if (record.status === 'added') return <ReviewedState verified={Boolean(toReviewed)} />;
  if (record.status === 'removed') return <ReviewedState verified={Boolean(fromReviewed)} />;
  if (Boolean(fromReviewed) === Boolean(toReviewed)) {
    const verified = Boolean(toReviewed);
    return (
      <TooltipTrigger
        text={verified ? 'Verified on both versions' : 'Not-Verified on both versions'}
      >
        <ReviewedState verified={verified} />
      </TooltipTrigger>
    );
  }
  return (
    <span className="vdm-reviewed-transition">
      <ReviewedState verified={Boolean(fromReviewed)} struck />
      <SparkIcon slug="arrow-right" size="small" />
      <ReviewedState verified={Boolean(toReviewed)} />
    </span>
  );
};

/**
 * Annotations cell: on single-label classification a changed label renders as
 * an `old → new` transition, all tags drop the redundant `(1)` count, and an
 * unchanged label gets a "'<label>' on both versions" tooltip; everything else
 * keeps the added/removed tag row.
 */
const AnnotationsCell = ({ record, singleLabel, allLabels }) => {
  const transition = singleLabel ? buildSingleLabelTransition(record) : null;
  if (transition) {
    return <LabelTransition transition={transition} allLabels={allLabels} />;
  }
  const tags = buildAnnotationTags(record, { hideCounts: singleLabel }).map((tag) =>
    singleLabel && tag.kind === 'plain'
      ? { ...tag, tooltip: `'${tag.label}' on both versions` }
      : tag,
  );
  return <AnnotationTagRow tags={tags} allLabels={allLabels} />;
};

/**
 * Response cell (generative tasks): the summary/output word-diff rendered with
 * the same added/removed styling as the EXAMPLE column; unchanged responses
 * show as plain text, absent ones as "-".
 */
const ResponseCell = ({ record }) => {
  const responseText = record.to?.response_text ?? record.from?.response_text ?? '';
  if (!record.response_diff?.length && !responseText) {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }
  return (
    <ClampedContent maxLines={3} className="vdm-example-clamp">
      {record.response_diff?.length ? (
        <DiffText segments={record.response_diff} view="both" />
      ) : (
        <Text variant="base-regular" color="primary">{responseText}</Text>
      )}
    </ClampedContent>
  );
};

/**
 * True when the record has content changes worth opening the Record Diff modal
 * for. The response diff only counts on generative tasks — it is the only
 * place the modal renders it.
 */
const hasViewableDiff = (record, outputLabel) =>
  record.status !== 'changed' ||
  Boolean(record.text_diff?.length) ||
  (Boolean(outputLabel) && Boolean(record.response_diff?.length)) ||
  Boolean(record.annotations);

/**
 * "Changed Records (N)" collapsible section: paginated table of added / removed /
 * changed train-set records with diff-highlighted example text (Figma node 5208:32508).
 * On generative tasks (`outputLabel` set) the annotations column becomes the
 * summary/output diff column instead.
 */
const ChangedRecordsSection = ({
  records = [],
  total = 0,
  page = 1,
  pageSize = 10,
  loading = false,
  annotationsKind = 'labels',
  outputLabel = null,
  singleLabel = false,
  onPageChange,
  onPageSizeChange,
  onViewRecord,
  allLabels = [],
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const annotationsHeader =
    outputLabel || (annotationsKind === 'entities' ? 'Entities' : 'Labels');

  const exampleText = (record) => record.to?.text ?? record.from?.text ?? '';

  return (
    <DiffSection title={`Changed Records (${total})`} defaultOpen>
      {loading ? (
        <div className="vdm-loading"><Spinner size="small" /></div>
      ) : total === 0 ? (
        <Text variant="base-regular" color="secondary">
          No record changes between these versions
        </Text>
      ) : (
        <>
          <div className="vdm-table vdm-records-table" role="table">
            <div className="vdm-table-header-row" role="row">
              <Text variant="uniq-semibold-table-caps" color="secondary" role="columnheader">
                Example
              </Text>
              <Text variant="uniq-semibold-table-caps" color="secondary" role="columnheader">
                {annotationsHeader}
              </Text>
              <Text variant="uniq-semibold-table-caps" color="secondary" role="columnheader">
                Reviewed
              </Text>
              <span role="columnheader" />
            </div>
            {records.map((record) => (
              <div className="vdm-table-row" role="row" key={record.record_id}>
                {/* Copying an example happens in the Record Diff modal (eye icon). */}
                <div className="vdm-example-cell" role="cell">
                  <ClampedContent maxLines={3} className="vdm-example-clamp">
                    {record.text_diff?.length ? (
                      <DiffText segments={record.text_diff} view="both" />
                    ) : (
                      <Text variant="base-regular" color="primary">{exampleText(record)}</Text>
                    )}
                  </ClampedContent>
                </div>
                {outputLabel ? (
                  <div className="vdm-example-cell" role="cell">
                    <ResponseCell record={record} />
                  </div>
                ) : (
                  <span role="cell">
                    <AnnotationsCell
                      record={record}
                      singleLabel={singleLabel}
                      allLabels={allLabels}
                    />
                  </span>
                )}
                <span role="cell">
                  <ReviewedCell record={record} />
                </span>
                <span role="cell" className="vdm-row-actions">
                  {hasViewableDiff(record, outputLabel) ? (
                    <Button
                      variant="ghost"
                      size="tiny"
                      type="button"
                      aria-label="View record diff"
                      icon={<SparkIcon slug="eye" size="small" />}
                      onClick={() => onViewRecord?.(record)}
                    />
                  ) : (
                    // A disabled Button suppresses its built-in tooltip, so the
                    // "No changes made" hint needs an explicit outer trigger.
                    <TooltipTrigger text="No changes made">
                      <span>
                        <Button
                          variant="ghost"
                          size="tiny"
                          type="button"
                          aria-label="View record diff"
                          disabled
                          icon={<SparkIcon slug="eye" size="small" />}
                        />
                      </span>
                    </TooltipTrigger>
                  )}
                </span>
              </div>
            ))}
          </div>
          {total > pageSize || page > 1 ? (
            <Pagination
              type="total-items"
              currentPage={page}
              totalPages={totalPages}
              pages={buildPageItems(totalPages, page)}
              totalItems={total}
              pageSize={pageSize}
              pageSizeLabel={`${pageSize} / Page`}
              onPageChange={onPageChange}
              onPrevious={() => onPageChange?.(page - 1)}
              onNext={() => onPageChange?.(page + 1)}
              onPageSizeChange={onPageSizeChange}
              className="vdm-pagination"
            />
          ) : null}
        </>
      )}
    </DiffSection>
  );
};

export default ChangedRecordsSection;
